import clsx from 'clsx';
import pluralize from 'pluralize';
import React, { useState } from 'react';

import FlagIcon from 'images/flag.svg';
import ShareIcon from 'images/share.svg';

import FlexLink from '@components/FlexLink';
import IconComment from '@components/Images/Rebranding/IconComment';
import { useAppContext } from '@contexts/AppContext';
import { useCommentActions, useCurrentUser, useForumPostPinnedToggle } from '@hooks';
import { edit_community_comment_path, edit_forum_post_path, forum_post_path } from '@routes';
import { ForumPost } from '@utils/backend-api/forumPosts';

import LikesCount from '../LikesCount';
import ShareModal from '../ShareModal';
import SuperPinModal from '../SuperPinModal';
import styles from './styles.module.scss';

export default function ForumPostActions({
  forumPost,
  minimal,
  onDownloadScreenshot,
}: {
  forumPost: ForumPost;
  minimal?: boolean;
  onDownloadScreenshot?: (darkMode?: boolean) => void;
}) {
  const { env } = useAppContext();
  const { currentUser } = useCurrentUser();
  const { comment } = forumPost;
  const [isShareModalOpen, setIsSharedModalOpen] = useState(false);
  const [isSuperPinModalOpen, setIsSuperPinModalOpen] = useState(false);
  const { mutate: toggleForumPostPinned, isLoading: isPinLoading } = useForumPostPinnedToggle();
  const { report, toggleLike, liked, likesCount, areLikesLoading } = useCommentActions(comment);

  const commentsCount = forumPost.total_comments_count;
  const isAdmin = currentUser?.administrator?.is_active;

  const onTogglePinClick = () => {
    window.confirm(
      `Are you sure you want to ${forumPost.pinned_until ? 'unpin' : 'pin'} this post${
        forumPost.pinned_until ? '' : ' for the next 24 hours'
      }?`
    ) && toggleForumPostPinned(forumPost.id);
  };

  return (
    <div
      className={clsx(styles.actions, {
        [styles.commentActionsMinimal]: minimal,
      })}
    >
      <div className={styles.actionContainer}>
        <button
          onClick={toggleLike}
          disabled={!!(areLikesLoading || env.READ_ONLY_MODE || !currentUser?.permitted_to_comment)}
          title="Like this post"
          className={clsx(styles.likeButton, styles.action, 'unstyled')}
        >
          <span
            className={clsx({
              [styles.likesCountLiked]: liked,
            })}
          >
            <LikesCount count={likesCount} commentId={comment.id} />
          </span>
          {!minimal && (
            <span
              className={clsx(styles.smallAction, {
                [styles.likesCountLiked]: liked,
              })}
            >
              {pluralize('like', likesCount === 0 ? 1 : likesCount)}
            </span>
          )}
        </button>
      </div>
      {minimal ? (
        <div className={styles.actionContainer}>
          <FlexLink
            href={forum_post_path(forumPost.id, { anchor: 'comments' })}
            className={styles.action}
          >
            <IconComment className="w-16" />
            {commentsCount}
            <span className={styles.smallAction}>{pluralize('Comment', commentsCount)}</span>
          </FlexLink>
        </div>
      ) : (
        <>
          {commentsCount > 0 && (
            <div className={styles.actionContainer}>
              <div className={styles.action}>
                <IconComment className="w-16" />
                {commentsCount}
                <span className={styles.smallAction}>{pluralize('Comment', commentsCount)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {!minimal && (
        <>
          <div className={styles.actionContainer}>
            <button
              className={clsx('unstyled', styles.action)}
              onClick={() => setIsSharedModalOpen(true)}
            >
              <img src={ShareIcon} alt="Share" />
              <span className={styles.smallAction}>Share</span>
            </button>
          </div>
          <div className={styles.actionContainer}>
            <button onClick={report} className={clsx(styles.action, 'unstyled')}>
              <img src={FlagIcon} alt="Report" />
              <span className={styles.smallAction}>Report</span>
            </button>
          </div>
          {isAdmin && (
            <>
              <div className={styles.actionContainer}>
                <a href={edit_community_comment_path(comment.id)} className={styles.action}>
                  Hide
                </a>
              </div>
              <div className={styles.actionContainer}>
                <button
                  onClick={onTogglePinClick}
                  className={styles.action}
                  disabled={isPinLoading}
                >
                  {forumPost.pinned_until ? 'Unpin' : 'Pin'}
                </button>
              </div>
              <div className={styles.actionContainer}>
                <button
                  onClick={() => setIsSuperPinModalOpen(true)}
                  className={styles.action}
                  disabled={forumPost.super_pinned || !forumPost.email_digest_enabled}
                >
                  {forumPost.super_pinned ? 'Super Pinned' : 'Super Pin'}
                </button>
              </div>
              <div className={styles.actionContainer}>
                <FlexLink href={edit_forum_post_path(forumPost.id)} className={styles.action}>
                  Manage
                </FlexLink>
              </div>
            </>
          )}
          {!!onDownloadScreenshot && (
            <div className={styles.actionContainer}>
              <button
                onClick={() => onDownloadScreenshot()}
                className={clsx(styles.action, 'unstyled')}
                title="Download as shareable screenshot essay"
              >
                Download
              </button>
            </div>
          )}
        </>
      )}

      <ShareModal
        forumPost={forumPost}
        isOpen={isShareModalOpen}
        setIsOpen={setIsSharedModalOpen}
        onDownloadScreenshot={onDownloadScreenshot}
      />
      <SuperPinModal
        forumPostId={forumPost.id}
        isOpen={isSuperPinModalOpen}
        setIsOpen={setIsSuperPinModalOpen}
      />
    </div>
  );
}
